"use client";

import { motion } from "framer-motion";
import { useComponentTranslations } from "@/app/components/LanguageProvider";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";
import PageHero from "@/app/components/PageHero";
import PageContainer from "@/app/components/PageContainer";
import PageSection from "@/app/components/PageSection";
import SectionTitle from "@/app/components/SectionTitle";
import GlassCard from "@/app/components/GlassCard";
import Image from "next/image";
import BookingForm from "@/app/components/BookingForm";

// const IndustriesSection = dynamic(() => import('@/app/components/IndustriesSection'), {
//   ssr: false,
//   loading: () => <div className="py-16 sm:py-24 pb-20"><div className="animate-pulse h-48 bg-white/5 rounded-xl max-w-7xl mx-auto px-4" /></div>
// });

type TitleKey =
    | "industryFinanceTitle"
    | "industryTelecomTitle"
    | "industryHospitalityTitle"
    | "industryLogisticsTitle"
    | "industryRetailTitle"
    | "industryHealthcareTitle"
    | "industryEducationTitle"
    | "industryMarketingTitle"
    | "industrySportsTitle"
    | "industryAgricultureTitle"
    | "industryUtilitiesTitle"
    | "industryInsuranceTitle";

type SolutionsKey =
    | "industryFinanceSolutions"
    | "industryTelecomSolutions"
    | "industryHospitalitySolutions"
    | "industryLogisticsSolutions"
    | "industryRetailSolutions"
    | "industryHealthcareSolutions"
    | "industryEducationSolutions"
    | "industryMarketingSolutions"
    | "industrySportsSolutions"
    | "industryAgricultureSolutions"
    | "industryUtilitiesSolutions"
    | "industryInsuranceSolutions";

const industries: { slug: string; titleKey: TitleKey; solutionsKey: SolutionsKey; image: string }[] = [
    { slug: "finance", titleKey: "industryFinanceTitle", solutionsKey: "industryFinanceSolutions", image: "/images/finance.jpg" },
    { slug: "telecom", titleKey: "industryTelecomTitle", solutionsKey: "industryTelecomSolutions", image: "/images/telecom.jpg" },
    { slug: "hospitality", titleKey: "industryHospitalityTitle", solutionsKey: "industryHospitalitySolutions", image: "/images/hospitality.jpg" },
    { slug: "logistics", titleKey: "industryLogisticsTitle", solutionsKey: "industryLogisticsSolutions", image: "/images/logistics.jpg" },
    { slug: "retail", titleKey: "industryRetailTitle", solutionsKey: "industryRetailSolutions", image: "/images/retail.jpg" },
    { slug: "healthcare", titleKey: "industryHealthcareTitle", solutionsKey: "industryHealthcareSolutions", image: "/images/healthcare.jpeg" },
    { slug: "education", titleKey: "industryEducationTitle", solutionsKey: "industryEducationSolutions", image: "/images/education.jpg" },
    { slug: "marketing", titleKey: "industryMarketingTitle", solutionsKey: "industryMarketingSolutions", image: "/images/marketing.jpg" },
    { slug: "sports", titleKey: "industrySportsTitle", solutionsKey: "industrySportsSolutions", image: "/images/sports.jpg" },
    { slug: "agriculture", titleKey: "industryAgricultureTitle", solutionsKey: "industryAgricultureSolutions", image: "/images/agriculture.jpg" },
    { slug: "utilities", titleKey: "industryUtilitiesTitle", solutionsKey: "industryUtilitiesSolutions", image: "/images/utilities.jpg" },
    { slug: "insurance", titleKey: "industryInsuranceTitle", solutionsKey: "industryInsuranceSolutions", image: "/images/insurance.jpg" },
];

export default function IndustriesClient() {
    const { t: industriesT, translationsLoaded: industriesLoaded } = useComponentTranslations("Industries");
    const { t: commonT, translationsLoaded: commonLoaded } = useComponentTranslations("Common");
    const locale = useLocale();

    const indT = (key: string, fallback?: string) => {
        if (!industriesLoaded) return fallback ?? key;
        return industriesT(key, fallback) ?? fallback ?? key;
    };

    const cT = (key: string, fallback?: string) => {
        if (!commonLoaded) return fallback ?? key;
        return commonT(key, fallback) ?? fallback ?? key;
    };

    return (
        <div className="min-h-screen text-white">
            {/* Hero Section */}
            <PageContainer>
                <PageHero
                    id="industries-hero"
                    title={indT("heroTitle")}
                    description={indT("heroDescription")}
                    actions={[
                        {
                            href: getPathWithLocale("/book-call?source=industries", locale),
                            label: locale.startsWith("ru") ? "Обсудить проект" : "Discuss a project",
                            primary: true,
                            ctaLocation: "pagehero_industries_primary",
                        },
                    ]}
                >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left">
                            <div className="text-2xl font-bold text-white md:text-3xl">50+</div>
                            <div className="text-sm text-white/60">{indT("statsProjects")}</div>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left">
                            <div className="text-2xl font-bold text-white md:text-3xl">12+</div>
                            <div className="text-sm text-white/60">{indT("statsIndustries")}</div>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left">
                            <div className="text-2xl font-bold text-white md:text-3xl">25+</div>
                            <div className="text-sm text-white/60">{indT("statsYears")}</div>
                        </div>
                    </div>
                </PageHero>
            </PageContainer>

            {/* Industries Grid */}
            <PageSection>
                    <SectionTitle title={cT("industriesTitle", "Industries")} />

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {industries.map((industry, index) => (
                            <motion.div
                                key={industry.slug}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                            >
                                <GlassCard
                                    href={getPathWithLocale(`/industries/${industry.slug}`, locale)}
                                    padded={false}
                                    className="h-full overflow-hidden"
                                >
                                    <div className="relative h-40 w-full">
                                        <Image
                                            src={industry.image}
                                            alt={indT(industry.titleKey)}
                                            fill
                                            className="object-cover"
                                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                        />
                                    </div>
                                    <div className="p-5 md:p-6">
                                        <h3 className="text-base font-semibold text-white">
                                            {indT(industry.titleKey)}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-white/60 line-clamp-3">
                                            {indT(industry.solutionsKey)}
                                        </p>
                                    </div>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
            </PageSection>

            {/* Advantages Section */}
            <PageSection>
                    <SectionTitle title={indT("advantagesTitle")} />

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {[
                            { title: "advantage1Title", desc: "advantage1Desc" },
                            { title: "advantage2Title", desc: "advantage2Desc" },
                            { title: "advantage3Title", desc: "advantage3Desc" },
                            { title: "advantage4Title", desc: "advantage4Desc" },
                        ].map((advantage, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <GlassCard className="h-full">
                                    <h3 className="text-base font-semibold text-white">
                                        {indT(advantage.title)}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                                        {indT(advantage.desc)}
                                    </p>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
            </PageSection>

            {/* What Happens Next Section */}
            <PageSection>
                    <SectionTitle title={indT("whatHappensNextTitle")} />

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {[
                            { title: "step1Title", desc: "step1Desc", number: "1" },
                            { title: "step2Title", desc: "step2Desc", number: "2" },
                            { title: "step3Title", desc: "step3Desc", number: "3" },
                            { title: "step4Title", desc: "step4Desc", number: "4" },
                        ].map((step, index) => (
                            <motion.div
                                key={index}
                                className="relative"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <GlassCard className="h-full">
                                    <div className="text-2xl font-bold text-white/40">{step.number}</div>
                                    <h3 className="mt-2 text-base font-semibold text-white">
                                        {indT(step.title)}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                                        {indT(step.desc)}
                                    </p>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
            </PageSection>

            {/* CTA Section */}
            <PageSection>
                <motion.div
                    className="max-w-3xl"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <SectionTitle title={indT("ctaTitle")} description={indT("ctaDescription")} />
                    <GlassCard className="p-6 md:p-8">
                        <BookingForm showSuccessLink={false} />
                    </GlassCard>
                </motion.div>
            </PageSection>
        </div>
    );
}
